// src/pages/AdminPanel.jsx — Advanced administration (RBAC, branches, ops, health, audit)
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import PurchaseOrdersList from "../components/PurchaseOrdersList";
import { firebaseProjectId } from "../firebase";
import {
  adminGetCollectionCounts,
  adminQueryAuditLogs,
} from "../services/adminApi";
import { OperationsTab, HealthTab, AuditTab } from "./admin/AdminPanelOps";
import AdminUsersSection from "./admin/AdminUsersSection";
import AdminLoginsAppTab from "./admin/AdminLoginsAppTab";
import BossDashboardActivityTab from "./admin/BossDashboardActivityTab";
import { PERMISSION_IDS } from "../config/permissions";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "users", label: "Users & access" },
  { id: "operations", label: "Operations" },
  { id: "health", label: "Data & health" },
  { id: "logins-app", label: "Logins (App)" },
  { id: "boss-dashboard", label: "Boss dashboard" },
  { id: "audit", label: "Audit" },
  { id: "firestore-manager", label: "Firestore manager" },
  { id: "purchase-orders", label: "Purchase orders" },
];

const TAB_META = {
  overview: "Project controls and admin capabilities",
  users: "Manage users, permissions, branch access, and payroll screen reference",
  operations: "Maintenance mode and announcement banner controls",
  health: "Collection counts, backup links, and data checks",
  "logins-app": "Track app login telemetry from Firestore logins collection",
  "boss-dashboard": "Boss mobile dashboard sessions, page views, and actions",
  audit: "View and export audit trail events",
  "firestore-manager": "Firestore Admin Console — collections, backups, query builder, audit",
  "purchase-orders": "Read-only purchase orders overview",
};

const consoleBase = `https://console.firebase.google.com/project/${firebaseProjectId}`;

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();

  const onTabClick = (tabId) => {
    if (tabId === "firestore-manager") {
      navigate("/admin/firestore-manager");
      return;
    }
    setActiveTab(tabId);
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-slate-100 via-slate-50 to-white">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />

      <main className="flex-1 overflow-auto px-4 py-6 sm:px-6 lg:px-8" aria-label="Administration">
        <div className="mx-auto max-w-7xl space-y-6">
          <header className="overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-6 text-white shadow-lg sm:p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-200/90">Administration</p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Advanced admin panel</h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-indigo-100/90">
              Manage users, granular permissions ({PERMISSION_IDS.length} flags), branch visibility for orders, Firebase Auth
              sync, app-wide operations (maintenance &amp; banners), Firestore health, and audit trails.
            </p>
            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              <div className="rounded-xl border border-white/15 bg-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-indigo-200">Sections</p>
                <p className="mt-0.5 text-sm font-semibold text-white">{TABS.length} admin workspaces</p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-indigo-200">Permissions</p>
                <p className="mt-0.5 text-sm font-semibold text-white">{PERMISSION_IDS.length} granular flags</p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-indigo-200">Current section</p>
                <p className="mt-0.5 text-sm font-semibold text-white">{TABS.find((x) => x.id === activeTab)?.label || "Overview"}</p>
              </div>
            </div>
          </header>

          <div
            className="sticky top-0 z-10 flex flex-wrap gap-1.5 rounded-2xl border border-slate-200/80 bg-slate-100/95 p-1.5 backdrop-blur"
            role="tablist"
            aria-label="Admin sections"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={activeTab === t.id}
                title={TAB_META[t.id]}
                onClick={() => onTabClick(t.id)}
                className={`inline-flex rounded-xl px-3.5 py-2.5 text-sm font-semibold transition ${
                  activeTab === t.id
                    ? "bg-white text-indigo-800 shadow-sm ring-1 ring-slate-200/80"
                    : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <section className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active workspace</p>
                <h2 className="text-lg font-semibold text-slate-900">
                  {TABS.find((x) => x.id === activeTab)?.label || "Overview"}
                </h2>
                <p className="mt-0.5 text-sm text-slate-600">{TAB_META[activeTab]}</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab("overview")}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                Back to overview
              </button>
            </div>
          </section>

          {activeTab === "overview" && (
            <div className="grid gap-6 lg:grid-cols-2">
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Project</h2>
                <p className="mt-2 font-mono text-sm text-slate-700">{firebaseProjectId}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to="/admin/projects"
                    className="inline-flex rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-900 hover:bg-violet-100"
                  >
                    Firestore Studio — Projects
                  </Link>
                  <Link
                    to="/admin/firestore-manager"
                    className="inline-flex rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-100"
                  >
                    Firestore Data Manager
                  </Link>
                  <a
                    href={`${consoleBase}/overview`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                  >
                    Firebase console
                  </a>
                  <a
                    href={`${consoleBase}/authentication/users`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    Authentication
                  </a>
                  <a
                    href={`${consoleBase}/firestore`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    Firestore
                  </a>
                </div>
              </section>
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Capabilities</h2>
                <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-slate-600">
                  <li>
                    <strong className="text-slate-800">Users &amp; access</strong> — granular permissions, branch limits,
                    bcrypt passwords, invite / reset links, Auth sync, CSV export.
                  </li>
                  <li>
                    <strong className="text-slate-800">Operations</strong> — maintenance mode, timed announcement banner.
                  </li>
                  <li>
                    <strong className="text-slate-800">Data &amp; audit</strong> — collection counts (via Cloud Function),
                    audit log query &amp; export.
                  </li>
                  <li>
                    <strong className="text-slate-800">Boss dashboard</strong> — mobile boss app activity (
                    <code className="rounded bg-slate-100 px-1 text-xs">boss_dashboard_activities</code>).
                  </li>
                  <li>
                    <strong className="text-slate-800">Payroll</strong> — <strong>Monitor</strong> (monthly attendance at{" "}
                    <code className="rounded bg-slate-100 px-1 text-xs">/payroll</code>),{" "}
                    <strong>Monthly report</strong> (all staff absent days at{" "}
                    <code className="rounded bg-slate-100 px-1 text-xs">/payroll/monthly-report</code>). Branch limits on
                    Users &amp; access apply to both.
                  </li>
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("users");
                    toast.success("Open the Users tab to manage accounts");
                  }}
                  className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-900 hover:bg-indigo-100"
                >
                  Go to Users &amp; access
                </button>
              </section>
            </div>
          )}

          {activeTab === "users" && <AdminUsersSection />}

          {activeTab === "operations" && <OperationsTab />}

          {activeTab === "health" && <HealthTab getCollectionCounts={adminGetCollectionCounts} />}

          {activeTab === "logins-app" && <AdminLoginsAppTab />}

          {activeTab === "boss-dashboard" && <BossDashboardActivityTab />}

          {activeTab === "audit" && <AuditTab queryAuditLogs={adminQueryAuditLogs} />}

          {activeTab === "purchase-orders" && (
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="mb-2 text-lg font-semibold text-slate-900">Purchase orders</h2>
              <p className="mb-4 text-sm text-slate-500">Read-only overview; full workflow lives under Transactions.</p>
              <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                <PurchaseOrdersList />
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
